import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import {
  Loader2, Settings, Users, CalendarDays, Plus, Save, X, Edit2, Palette, Search,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { LeaveCalendarPanel } from "@/pages/employee/ViewLeaveCalendar";
import { yearOptions } from "@/lib/years";

const TABS = [
  { id: "calendar", label: "Calendar concedii", icon: CalendarDays },
  { id: "types", label: "Tipuri concediu", icon: Settings },
  { id: "balances", label: "Solduri angajați", icon: Users },
];

const RO_MONTHS = ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie",
  "Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"];

// "Cosmin Mogos" -> "C.Mogos" (inițiala prenumelui + numele întreg)
function shortName(full) {
  if (!full) return "";
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0][0].toUpperCase()}.${parts.slice(1).join(" ")}`;
}

export default function TimeOffAdmin() {
  const [tab, setTab] = useState("calendar");

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand mb-2">HR Admin</p>
          <h1 className="font-display font-semibold text-[38px] tracking-[-0.02em] leading-tight text-ink mb-2">Concedii & absențe — Administrare</h1>
          <p className="text-ink-muted text-sm">
            Configurează tipuri de absență și gestionează soldurile angajaților.
          </p>
        </div>

        <div className="flex gap-1 border-b border-neutral-200 mb-6 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 whitespace-nowrap shrink-0 ${
                  tab === t.id ? "border-brand text-brand" : "border-transparent text-ink-muted hover:text-ink"
                }`}>
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "calendar" && <LeaveCalendarPanel />}
        {tab === "types" && <TypesTab />}
        {tab === "balances" && <BalancesTab />}
      </div>
    </PortalLayout>
  );
}


function TypesTab() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/timeoff/types"); setTypes(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => setCreating(true)}
          data-testid="btn-new-type"
          className="inline-flex items-center gap-2 bg-brand text-white px-4 py-2 text-sm font-semibold">
          <Plus className="h-4 w-4" /> Tip nou
        </button>
      </div>
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-brand mx-auto my-8" /> : (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Nume</th>
                <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Zile/an</th>
                <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Consumă sold</th>
                <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Necesită doc.</th>
                <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Activ</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {types.map(t => (
                <tr key={t.id} className="border-b border-neutral-100">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3" style={{ background: t.color }} />
                      <span className="font-medium">{t.name}</span>
                    </div>
                  </td>
                  <td className="p-3">{t.days_per_year}</td>
                  <td className="p-3">{t.deducts_from_balance ? "Da" : "Nu"}</td>
                  <td className="p-3">{t.requires_document ? "Da" : "Nu"}</td>
                  <td className="p-3">{t.is_active ? "✓" : "✗"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => setEdit(t)} className="text-brand text-xs hover:underline">Editează</button>
                    <button
                      data-testid={`delete-type-${t.code}`}
                      onClick={async () => {
                        if (!window.confirm(`Ștergi definitiv tipul „${t.name}"? Soldurile aferente vor fi eliminate.`)) return;
                        try {
                          await api.delete(`/timeoff/types/${t.id}`);
                          toast.success("Tip șters");
                          await load();
                        } catch (e) { toast.error(apiErr(e)); }
                      }}
                      className="text-red-600 text-xs hover:underline ml-3">Șterge</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TypeForm open={creating || !!edit} type={edit}
        onClose={() => { setCreating(false); setEdit(null); }}
        onSaved={async () => { setCreating(false); setEdit(null); await load(); }}
      />
    </div>
  );
}


function TypeForm({ open, type, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const isEdit = !!type;

  useEffect(() => {
    if (open) {
      setForm(type || {
        code: "", name: "", days_per_year: 0,
        deducts_from_balance: true, requires_document: false,
        color: "#3d68b1", unit: "days", is_active: true,
      });
    }
  }, [open, type]);

  async function save() {
    try {
      if (isEdit) {
        await api.put(`/timeoff/types/${type.id}`, form);
        toast.success("Tip actualizat");
      } else {
        if (!form.code || !form.name) { toast.error("Cod și nume obligatorii"); return; }
        await api.post("/timeoff/types", form);
        toast.success("Tip creat");
      }
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  }

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editează tip" : "Tip nou"}</DialogTitle>
          <DialogDescription>Configurează un tip de absență disponibil pentru angajați.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono uppercase text-ink-muted">Cod *</label>
              <input value={form.code || ""} disabled={isEdit}
                onChange={e => setForm({ ...form, code: e.target.value })}
                className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase text-ink-muted">Nume *</label>
              <input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono uppercase text-ink-muted">Zile/an</label>
              <input type="number" step="0.5" value={form.days_per_year || 0}
                onChange={e => setForm({ ...form, days_per_year: parseFloat(e.target.value) || 0 })}
                className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase text-ink-muted">Culoare</label>
              <input type="color" value={form.color || "#3d68b1"}
                onChange={e => setForm({ ...form, color: e.target.value })}
                className="w-full mt-1 border border-neutral-300 px-3 py-1 h-[38px]" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.deducts_from_balance}
                onChange={e => setForm({ ...form, deducts_from_balance: e.target.checked })} />
              Consumă sold
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.requires_document}
                onChange={e => setForm({ ...form, requires_document: e.target.checked })} />
              Doc. obligatoriu
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.is_active}
                onChange={e => setForm({ ...form, is_active: e.target.checked })} />
              Activ
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-ink-muted">Anulează</button>
          <button onClick={save} className="px-4 py-2 text-sm bg-brand text-white inline-flex items-center gap-2">
            <Save className="h-4 w-4" /> Salvează
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function BalancesTab() {
  const [users, setUsers] = useState([]);
  const [selUser, setSelUser] = useState(null);
  const [balances, setBalances] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  async function loadUsers() {
    try {
      const { data } = await api.get("/staff/users");
      setUsers(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }

  async function loadBalance(uid) {
    try {
      const { data } = await api.get(`/timeoff/balances/${uid}?year=${year}`);
      setBalances(data.balances);
    } catch (e) { toast.error(apiErr(e)); }
  }

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { if (selUser) loadBalance(selUser.id); }, [selUser, year]);

  async function saveBalance(typeId, patch) {
    try {
      await api.put(`/timeoff/balances/${selUser.id}/${typeId}?year=${year}`, patch);
      toast.success("Sold actualizat");
      setEditing(null);
      await loadBalance(selUser.id);
    } catch (e) { toast.error(apiErr(e)); }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-white border border-neutral-200 max-h-[600px] overflow-y-auto">
        <div className="p-3 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <div className="font-semibold text-sm mb-2">Angajați</div>
          <div className="relative">
            <Search className="h-4 w-4 text-ink-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              data-testid="balances-search" placeholder="Caută angajat..."
              className="w-full border border-neutral-200 pl-8 pr-2 py-1.5 text-sm focus:border-brand focus:outline-none" />
          </div>
        </div>
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-brand m-4" /> : (
          <div>
            {(() => {
              const s = search.trim().toLowerCase();
              const list = users.filter(u => u.role !== "client" &&
                (!s || [u.name, u.email, u.job_title].some(x => (x || "").toLowerCase().includes(s))));
              if (list.length === 0) return <div className="p-4 text-sm text-ink-muted">Niciun angajat găsit.</div>;
              return list.map(u => (
                <button key={u.id} onClick={() => setSelUser(u)}
                  className={`w-full text-left p-3 border-b border-neutral-100 hover:bg-neutral-50 ${
                    selUser?.id === u.id ? "bg-brand/5 border-l-2 border-l-brand" : ""
                  }`}>
                  <div className="text-sm font-medium">{u.name}</div>
                  <div className="text-xs text-ink-muted">{u.job_title || u.email}</div>
                </button>
              ));
            })()}
          </div>
        )}
      </div>

      <div className="lg:col-span-2">
        {!selUser ? (
          <div className="bg-white border border-neutral-200 p-12 text-center text-ink-muted">
            <Users className="h-8 w-8 mx-auto mb-3" />
            Selectează un angajat pentru a gestiona soldurile.
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="font-semibold text-lg">{selUser.name}</div>
                <div className="text-xs text-ink-muted">{selUser.job_title}</div>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-ink-muted">An:</label>
                <select value={year} onChange={e => setYear(parseInt(e.target.value))}
                  data-testid="balance-year" className="border border-neutral-300 px-2 py-1 text-sm bg-white">
                  {yearOptions(year).map(yr => <option key={yr} value={yr}>{yr}</option>)}
                </select>
              </div>
            </div>
            <div className="surface overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b">
                  <tr className="text-left">
                    <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Tip</th>
                    <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Total</th>
                    <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Reportat</th>
                    <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Ajustare</th>
                    <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Folosit</th>
                    <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">În așteptare</th>
                    <th className="p-3 font-display text-[12px] font-semibold text-ink-muted">Rămas</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {balances.map(b => {
                    const rec = b.record || {};
                    const isEdit = editing === b.type_id;
                    return (
                      <tr key={b.type_id} className="border-b border-neutral-100">
                        <td className="p-3 font-medium">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2" style={{ background: b.type.color }} />
                            {b.type.name}
                          </div>
                        </td>
                        {isEdit ? (
                          <>
                            <td className="p-2"><input type="number" step="0.5" defaultValue={rec.total_days || 0}
                              onBlur={e => rec.total_days = parseFloat(e.target.value)}
                              className="w-16 border border-neutral-300 px-2 py-1 text-xs" /></td>
                            <td className="p-2"><input type="number" step="0.5" defaultValue={rec.carryover_days || 0}
                              onBlur={e => rec.carryover_days = parseFloat(e.target.value)}
                              className="w-16 border border-neutral-300 px-2 py-1 text-xs" /></td>
                            <td className="p-2"><input type="number" step="0.5" defaultValue={rec.adjustment_days || 0}
                              onBlur={e => rec.adjustment_days = parseFloat(e.target.value)}
                              className="w-16 border border-neutral-300 px-2 py-1 text-xs" /></td>
                          </>
                        ) : (
                          <>
                            <td className="p-3">{rec.total_days ?? b.type.days_per_year}</td>
                            <td className="p-3">{rec.carryover_days || 0}</td>
                            <td className="p-3">{rec.adjustment_days || 0}</td>
                          </>
                        )}
                        <td className="p-3 text-emerald-700">{b.used}</td>
                        <td className="p-3 text-amber-700">{b.pending}</td>
                        <td className="p-3 font-semibold">{b.remaining}</td>
                        <td className="p-3">
                          {isEdit ? (
                            <button onClick={() => saveBalance(b.type_id, {
                              total_days: rec.total_days, carryover_days: rec.carryover_days,
                              adjustment_days: rec.adjustment_days,
                            })} className="text-xs bg-brand text-white px-2 py-1">Salvează</button>
                          ) : (
                            <button onClick={() => setEditing(b.type_id)} className="text-brand text-xs">Editează</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function CalendarTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/timeoff/calendar?month=${month}`);
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [month]);

  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();

  const byDay = useMemo(() => {
    const map = {};
    for (const r of items) {
      const start = new Date(r.start_date + "T00:00:00");
      const end = new Date(r.end_date + "T00:00:00");
      const monthStart = new Date(y, m - 1, 1);
      const monthEnd = new Date(y, m - 1, daysInMonth);
      const s = new Date(Math.max(start, monthStart));
      const e = new Date(Math.min(end, monthEnd));
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const key = d.getDate();
        (map[key] = map[key] || []).push(r);
      }
    }
    return map;
  }, [items, y, m, daysInMonth]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-semibold">{RO_MONTHS[m - 1]} {y}</div>
        <div className="flex gap-2">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="border border-neutral-300 px-3 py-1.5 text-sm" />
        </div>
      </div>
      {loading ? <Loader2 className="h-5 w-5 animate-spin text-brand" /> : (
        <div className="bg-white border border-neutral-200 grid grid-cols-7 gap-px bg-neutral-200">
          {["Lu","Ma","Mi","Jo","Vi","Sâ","Du"].map(d => (
            <div key={d} className="bg-neutral-50 p-2 text-center text-[11px] font-mono uppercase text-ink-muted">{d}</div>
          ))}
          {(() => {
            const firstDay = new Date(y, m - 1, 1);
            const firstWeekday = (firstDay.getDay() + 6) % 7;
            const cells = [];
            for (let i = 0; i < firstWeekday; i++) cells.push(<div key={`e${i}`} className="bg-white h-24" />);
            for (let d = 1; d <= daysInMonth; d++) {
              const items = byDay[d] || [];
              cells.push(
                <div key={d} className="bg-white h-24 p-1 overflow-hidden text-xs">
                  <div className="text-[11px] font-mono text-ink-muted mb-1">{d}</div>
                  <div className="space-y-0.5">
                    {items.slice(0, 3).map((r, i) => (
                      <div key={i} className="text-[10px] px-1 py-0.5 truncate"
                        style={{ background: (r.type?.color || "#3d68b1") + "22",
                                  borderLeft: `2px solid ${r.type?.color || "#3d68b1"}` }}
                        title={`${r.employee?.name} — ${r.type?.name}`}>
                        {shortName(r.employee?.name)}
                      </div>
                    ))}
                    {items.length > 3 && <div className="text-[10px] text-ink-muted">+{items.length - 3}</div>}
                  </div>
                </div>
              );
            }
            return cells;
          })()}
        </div>
      )}
    </div>
  );
}
